import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/**
 * Wrapper recharts pour les tendances temporelles (12 mois) avec une ou
 * plusieurs séries. Le composant choisit automatiquement l'axe Y et le
 * formateur (€ ou unité simple).
 */
export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  format?: 'number' | 'currency';
}

export interface TrendChartProps {
  data: Array<Record<string, string | number>>;
  xKey: string;
  series: TrendSeries[];
  height?: number;
}

function fmt(value: number, format: 'number' | 'currency' | undefined): string {
  if (format === 'currency') {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${value.toFixed(0)} €`;
    }
  }
  return new Intl.NumberFormat('fr-FR').format(value);
}

export function TrendChart({ data, xKey, series, height = 260 }: TrendChartProps) {
  if (data.length === 0) {
    return <p className="text-xs text-gray-500 py-8 text-center">Aucune donnée à afficher.</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} />
        <YAxis
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          width={60}
          tickFormatter={(v) => fmt(Number(v), series[0]?.format)}
        />
        <Tooltip
          contentStyle={{
            fontSize: 12,
            borderRadius: 8,
            border: '1px solid #e5e7eb',
          }}
          formatter={(value, name) => {
            const serie = series.find((s) => s.label === name);
            return [fmt(Number(value), serie?.format), String(name)];
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
